import { CheckCircle2, Loader2, XCircle } from "lucide-react";

import { cn } from "@/lib/utils";

import type { UploadStatus } from "../upload/types";

type UploadProgressStripProps = {
  status: UploadStatus;
};

/**
 * Pure visualisation of a single group's `UploadStatus`. The wizard owns
 * the per-group status map; this strip just renders the current step,
 * a progress bar when uploading, and either a success or failure tail.
 */
export function UploadProgressStrip({ status }: UploadProgressStripProps) {
  if (status.kind === "idle") {
    return (
      <div className="flex flex-row items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        <span className="inline-block h-2 w-2 rounded-full bg-muted-foreground/40" />
        <span>Pending</span>
      </div>
    );
  }

  if (status.kind === "creating") {
    return (
      <BusyStrip label="Creating batch…" />
    );
  }

  if (status.kind === "uploading") {
    const { total, done } = status;
    const percent = total === 0 ? 0 : Math.min(100, Math.round((done / total) * 100));
    return (
      <div className="flex flex-col gap-1 rounded-md border bg-primary/5 px-3 py-2 text-xs">
        <div className="flex flex-row items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" aria-hidden />
          <span className="font-medium text-primary">
            Uploading PDFs ({done}/{total})
          </span>
        </div>
        <div
          className="h-1.5 w-full rounded-full bg-primary/10 overflow-hidden"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full bg-primary transition-[width] duration-200"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    );
  }

  if (status.kind === "finalizing") {
    return <BusyStrip label="Finalizing…" />;
  }

  if (status.kind === "complete") {
    return (
      <div className="flex flex-col gap-0.5 rounded-md border border-green-600 bg-green-50 px-3 py-2 text-xs text-green-900">
        <div className="flex flex-row items-center gap-2">
          <CheckCircle2 className="h-3.5 w-3.5 text-green-700" aria-hidden />
          <span className="font-semibold">Complete</span>
        </div>
        <div className="text-[10px] font-mono text-green-900/80 break-all">
          batch_id: {status.batch_id}
        </div>
        <div className="text-[10px] font-mono text-green-900/80 break-all">
          process_job_id: {status.process_job_id}
        </div>
      </div>
    );
  }

  if (status.kind === "failed") {
    return (
      <div className="flex flex-col gap-0.5 rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-xs text-destructive">
        <div className="flex flex-row items-center gap-2">
          <XCircle className="h-3.5 w-3.5" aria-hidden />
          <span className="font-semibold">Failed</span>
        </div>
        <div className="break-words">{status.message}</div>
      </div>
    );
  }

  const _exhaustive: never = status;
  void _exhaustive;
  return null;
}

function BusyStrip({ label }: { label: string }) {
  return (
    <div
      className={cn(
        "flex flex-row items-center gap-2 rounded-md border bg-primary/5 px-3 py-2 text-xs",
      )}
    >
      <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" aria-hidden />
      <span className="font-medium text-primary">{label}</span>
    </div>
  );
}
